import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";

import Buttons from "./navigation/Buttons";
import TopSelector from "./navigation/TopSelector";
import Search from "./filters/Search";
import SubregionsSelect from "./filters/SubregionsSelect";
import OwnershipSelect from "./filters/OwnershipSelect";
import ServicesSelect from "./filters/ServicesSelect";
import FilteredUtilities from "./filters/FilteredUtilities";
import AllDataButton from "./navigation/AllDataButton";
import KeyFigures from "./summary/KeyFigures";
import UtilitySpending from "./summary/UtilitySpending";
import SectorStructure from "./summary/SectorStructure";
import ComparativeChart from "./comparative/ComparativeChart";
import CountryDropdown from "./navigation/CountryDropdown";
import AboutModal from "./AboutModal";
import DownloadsModal from "./DownloadsModal";
import MapContainer from "./map/MapContainer";
import ResetButton from "./navigation/ResetButton";

export const renderCountryOptions = options => {
  const optArray = [{ value: "all", label: "See all countries..." }];
  options.map(o => optArray.push({
    value: o.country_id,
    label: o.country_name
  }));
  return optArray;
};

class App extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      filters: {
        selectedCountryCode: this.props.data.globalData.selectedCountryCode || "all",
        selectedCountryName: this.props.data.globalData.selectedCountryName || "World",
        currentSortOption: "max_population_served",
        selectedOwnershipOptions: [],
        selectedSubregionOptions: [],
        selectedServicesOptions: [],
        searchValue: ""
      },
      currentType: "nrw_average",
      searchValue: "",
      currentTab: "dashboard",
      isAboutModalOpen: false,
      icons: this.props.icons,
      globalData: this.props.data.globalData,
      filteredUtilities: this.props.data.utilities,
      allUtilities: this.props.data.utilities,
      countryData: this.props.data.countries.find(country => country.country_id === this.props.data.globalData.selectedCountryCode) || {
        subregions: [],
        key_numbers: {},
      },
    };

    this.selectFromMap = this.selectFromMap.bind(this);
    this.changeTab = this.changeTab.bind(this);
    this.onReset = this.onReset.bind(this);
    this.onTypeChange = this.onTypeChange.bind(this);
    this.onCountryChange = this.onCountryChange.bind(this);
    this.onCountryDropdownChange = this.onCountryDropdownChange.bind(this);
    this.onSubregionChange = this.onSubregionChange.bind(this);
    this.sortBy = this.sortBy.bind(this);
    this.onOwnershipChange = this.onOwnershipChange.bind(this);
    this.onServicesChange = this.onServicesChange.bind(this);
    this.onSearchChange = this.onSearchChange.bind(this);
    this.updateFilters = this.updateFilters.bind(this);
    this.updateFilteredUtilities = this.updateFilteredUtilities.bind(this);
    this.showAboutModal = this.showAboutModal.bind(this);
    this.closeAboutModal = this.closeAboutModal.bind(this);
  }

  componentDidMount() {
    !this.props.data.globalData.preSelected && this.onReset();
    this.props.data.globalData.preSelected && this.updateFilters();
  }

  onReset() {
    this.setState({
      filters: {
        selectedCountryCode: "all",
        selectedCountryName: "World",
        currentSortOption: "max_population_served",
        selectedSubregionOptions: [],
        selectedOwnershipOptions: [],
        selectedServicesOptions: [],
        searchValue: ""
      },
      currentType: "nrw_average",
      countryData: this.props.data.globalData,
      filteredUtilities: _.orderBy(this.props.data.utilities, "max_population_served", "desc"),
    });
  }

  changeTab(e){
    this.setState({
      currentTab: e
    });
  }

  onTypeChange(e) {
    this.setState({
      currentType: e,
    });
  }

  updateFilters(filters) {
    this.setState(prevState => ({
      filters: {
        ...prevState.filters,
        ...filters
      }
    }), this.updateFilteredUtilities);
  }

  updateFilteredUtilities() {
    const {
      selectedCountryCode,
      selectedSubregionOptions,
      selectedOwnershipOptions,
      selectedServicesOptions,
      currentSortOption,
      searchValue
    } = this.state.filters;

    let newCountryData = this.props.data.countries;

    let newFilteredUtilities = this.props.data.utilities.filter((utility) => {
      const bools = [];

      if (selectedCountryCode === "all") {
        newCountryData = this.props.data.globalData;
      } else {
        bools.push(utility.country_id === selectedCountryCode);
        newCountryData = this.props.data.countries.find(country => country.country_id === selectedCountryCode);
      }

      if (selectedSubregionOptions.length > 0) {
        bools.push(
          selectedSubregionOptions.some((val) => {
            return utility.state_province === val.value;
          })
        );
      }

      if (selectedOwnershipOptions.length > 0) {
        bools.push(
          selectedOwnershipOptions.some((val) => {
            return utility.utility_type === val.value;
          })
        );
      }

      if (selectedServicesOptions.length > 0) {
        bools.push(
          selectedServicesOptions.some(option => utility[option.value])
        );
      }

      if (searchValue.length > 1) {
        // convert accented e's and such to regular chars to match the normalized strings from ruby
        const asciiSearchValue = searchValue.toLowerCase().normalize("NFD");
        bools.push(
          utility.search_string.indexOf(asciiSearchValue) !== -1
        );
      }

      return bools.every(e => e);
    });

    if (currentSortOption) {
      newFilteredUtilities = _.orderBy(newFilteredUtilities, currentSortOption, "desc");
    }

    this.setState({
      filteredUtilities: newFilteredUtilities,
      countryData: newCountryData
    });
  }

  onCountryChange(e) {
    if (e !== "all") {
      const countryData = this.props.data.countries.find(country => country.country_id === e);
      if(!countryData) {
        this.onReset();
        return;
      }

      this.updateFilters({
        selectedCountryCode: e,
        selectedCountryName: countryData.country_name,
      });
      this.onSubregionChange("");
      this.onOwnershipChange("");
      this.onServicesChange("");
    } else {
      this.onReset();
    }
  }

  onCountryDropdownChange(e) {
    return this.onCountryChange(e);
  }

  selectFromMap(e) {
    return this.onCountryChange(e);
  }

  sortBy(e) {
    this.updateFilters({ currentSortOption: e.value });
  }

  onSubregionChange(e) {
    if (e.length === 0) {
      this.updateFilters({ selectedSubregionOptions: [] });
    } else {
      this.updateFilters({ selectedSubregionOptions: e });
    }
  }

  onOwnershipChange(e) {
    if (e.length === 0) {
      this.updateFilters({ selectedOwnershipOptions: [] });
    } else {
      this.updateFilters({ selectedOwnershipOptions: e });
    }
  }

  onServicesChange(e) {
    if (e.length === 0) {
      this.updateFilters({ selectedServicesOptions: [] });
    } else {
      this.updateFilters({ selectedServicesOptions: e });
    }
  }

  onSearchChange(e) {
    this.updateFilters({ searchValue: e.currentTarget.value });
  }

  showAboutModal() {
    this.setState({
      isAboutModalOpen: true,
    });
  }

  closeAboutModal() {
    this.setState({
      isAboutModalOpen: false,
    });
  }

  render() {
    const {
      currentType,
      countryData,
      currentTab,
      filteredUtilities,
      icons,
      filters,
      isAboutModalOpen,
    } = this.state;

    const {
      selectedCountryCode,
      selectedCountryName,
      selectedOwnershipOptions,
      selectedServicesOptions,
      selectedSubregionOptions,
      searchValue,
      currentSortOption
    } = filters;

    const { countries } = this.props.data;

    return (
      <div id="utilities-dashboard">
        <div className="navigation">
          <Buttons onClick={this.changeTab} active={currentTab} showAboutModal={this.showAboutModal} />
        </div>
        <AboutModal
          showAboutModal={this.showAboutModal}
          closeAboutModal={this.closeAboutModal}
          isModalOpen={isAboutModalOpen}
        />
        {currentTab === "downloads" && <DownloadsModal countryData={countries} />}
        <div id="dashboard-container">
          <div className="dropdowns">
            <TopSelector
              countryOptions={countries}
              selectedCountryCode={selectedCountryCode}
              selectedType={currentType}
              onTypeChange={this.onTypeChange}
              onCountryChange={this.onCountryDropdownChange}
              onReset={this.onReset}
            />
          </div>
          <div className="main-container">
            <div className="filter-column">
              <div className="filters-wrapper">
                <div className="main-title">
                  Utilities
                </div>
                <div className="title">
                  <div className="selects-wrapper">
                    <Search onSearchChange={this.onSearchChange} value={searchValue} />
                    <CountryDropdown
                      onChange={this.onCountryDropdownChange}
                      options={renderCountryOptions(countries)}
                      placeholder="Select country"
                      selectedCountryCode={selectedCountryCode}
                    />
                    <SubregionsSelect
                      currentCountry={countryData}
                      onSubregionChange={this.onSubregionChange}
                      selectedSubregionOptions={selectedSubregionOptions}
                    />
                    <OwnershipSelect onOwnershipChange={this.onOwnershipChange} selectedOwnershipOptions={selectedOwnershipOptions} />
                    <ServicesSelect onServicesChange={this.onServicesChange} selectedServicesOptions={selectedServicesOptions} />
                    <ResetButton onReset={this.onReset} />
                  </div>
                </div>
              </div>
              <FilteredUtilities
                utilities={filteredUtilities}
                icons={icons}
                sortBy={this.sortBy}
                currentSortOption={currentSortOption}
              />
              <AllDataButton />
            </div>
            <div className="main-column">
              <MapContainer
                currentType={currentType}
                countries={countries}
                selectFromMap={this.selectFromMap}
                selectedCountryCode={selectedCountryCode}
              />
              <div className="summary-header">
                Country Summary
              </div>
              <div className="summary-body">
                {selectedCountryCode === "all" ?
                  <div className="select-a-country">
                    Please select a country
                  </div>
                  :
                  <div>
                    <div className="summary-left">
                      <KeyFigures data={countryData} icons={icons} />
                      {countryData.slug !== null &&
                        <UtilitySpending
                          data={countryData}
                          selectedCountryName={selectedCountryName}
                          numberOfCountries={countries.length}
                        />
                      }
                    </div>
                    <div className="summary-right">
                      {countryData.slug !== null &&
                        <SectorStructure
                          data={countryData.key_numbers}
                          icons={icons}
                          selectedCountryName={countryData.country_name}
                          slug={countryData.slug}
                        />
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
            <div className="right-column">
              <ComparativeChart
                data={countries}
                currentType={currentType}
                onCountryChange={this.onCountryChange}
                selectedCountryCode={selectedCountryCode}
              />
              {currentType === "extent_of_privatisation" &&
                <div className="info-box">
                  Based on utilities serving over 50,000 people rounded to the nearest 5%
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    );
  }
}

App.propTypes = {
  data: PropTypes.shape({
    countries: PropTypes.arrayOf(PropTypes.shape({})),
    globalData: PropTypes.shape({
      selectedCountryCode: PropTypes.string,
      selectedCountryName: PropTypes.string,
      preSelected: PropTypes.bool,
    }),
    utilities: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  icons: PropTypes.shape({}),
};

export default App;
